import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import html2canvas from 'html2canvas'
import mixpanel from 'mixpanel-browser'
import { ErrorBoundary } from 'react-error-boundary'
import * as THREE from 'three'

import { fetchImage } from '../../../../../api/buildingFetches'
import { FLOOR_PLAN_CHROME } from '../../../../../constants/floorPlanChrome'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { ProcoreAuthProvider } from '../../../../../contexts/procoreAuthContext'
import { usePannellumViewer } from '../../../../../hooks/usePannellum'
import useToggleOnEsc from '../../../../../hooks/useToggleOnEsc'
import { useViewportWidth } from '../../../../../hooks/useViewportWidth'
import { BREAKPOINTS } from '../../../../../styles/breakpoints'
import { PannellumViewer } from '../../../../../types'
import { createObjectURLSafe } from '../../../../../utils/imageUtils'
import { ComponentErrorFallback } from '../../../../common/Errors/ComponentErrorFallback'
import { LoadingIndicator } from '../../../../common/LoadingIndicator'
import { MinimapViewer } from '../../../../common/MapViewer'
import { Pannellum } from '../../../../third_party/Pannellum'
import { useImageViewerContext } from '../../imageViewerContext'
import { ViewerPosition } from '../../types'
import { DebugPanel } from '../DebugPanel'

import { ImageViewerContextMenu } from './components/ContextMenu/ImageViewerContextMenu'
import { ImageViewerSettings } from './components/ImageViewerSettings/ImageViewerSettings'
import { MinimapPopup } from './components/MinimapPopup'
import { TagManager } from './components/TagManager'
import { TourManager } from './components/TourManager'
import { XRayView } from './components/XRayView/XRayView'

interface ViewerPaneProps {
  type: string
  viewerPosition: ViewerPosition
  viewerPositionDelta?: ViewerPosition
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper imageData type
  imageData: any
  isSolo: boolean
  followRealtime: boolean
  syncedTo?: string
  onDragging: (isActive: boolean) => void
  onUpdate?: (newValues: ViewerPosition) => void
  minDate?: Date
  maxDate?: Date
  onRegisterScreenshot?: (fn: (callback: (blob: Blob) => void) => void) => void
  onRequestCameraScreenshot?: (callback: (blob: Blob) => void) => void
}
export const ViewerPane = ({
  type,
  viewerPosition,
  viewerPositionDelta,
  imageData,
  isSolo,
  followRealtime,
  syncedTo,
  onDragging,
  onUpdate,
  minDate,
  maxDate,
  onRegisterScreenshot,
  onRequestCameraScreenshot,
}: ViewerPaneProps) => {
  const { state: buildingState, updateImage } = useBuildingContext()
  const {
    state: imageViewerState,
    updateImageViewer,
    updateMaster,
    updatePane2,
  } = useImageViewerContext()
  const [loading, setLoading] = useState<boolean>(false)
  const mapRef = useRef<MinimapViewer | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper mapPopupRef type
  const mapPopupRef = useRef<any>(null)
  const [adjustedOffset, setAdjustedOffset] = useState<number | undefined>(
    undefined,
  )
  const { toggleActive, handleToggleKey } = useToggleOnEsc()

  const isMaster = useMemo(() => type === 'master', [type])

  const viewerId = useMemo(() => {
    return isMaster ? 'master' : 'subordinate'
  }, [isMaster])

  const { viewer } = usePannellumViewer(viewerId)
  const viewportWidth = useViewportWidth()

  const minimapRightPosition = useMemo(() => {
    if (viewportWidth < BREAKPOINTS.tablet) {
      return 16
    }
    return isMaster ? 40 : viewportWidth / 2 + 40
  }, [viewportWidth, isMaster])

  const imageViewerStateDetail = useMemo(() => {
    return imageViewerState[type]
  }, [imageViewerState, type])

  const pannellumAngleOffset = useMemo(() => {
    return imageViewerState[type].data?.angle || 0
  }, [imageViewerState, type])

  const angleOffsetDelta = useMemo(() => {
    if (adjustedOffset === undefined) {
      return 0
    }
    return pannellumAngleOffset - adjustedOffset
  }, [pannellumAngleOffset, adjustedOffset])

  /** When the angle debug panel is open, preview the edited offset on the panorama (not only on the minimap/tour). */
  const effectivePannellumAngleOffset = useMemo(() => {
    return adjustedOffset !== undefined ? adjustedOffset : pannellumAngleOffset
  }, [adjustedOffset, pannellumAngleOffset])

  const handleOffsetChange = useCallback((newOffset: number) => {
    setAdjustedOffset(newOffset)
  }, [])

  const handleAngleSavedToContext = useCallback(
    (nextAngle: number) => {
      const data = { ...imageData.data, angle: nextAngle }
      if (isMaster) {
        updateMaster({ data })
      } else {
        updatePane2({ data })
      }
      if (buildingState.imageData?.data?.id === imageData.data.id) {
        updateImage({
          data: {
            ...buildingState.imageData.data,
            angle: nextAngle,
          },
        })
      }
    },
    [
      buildingState.imageData,
      imageData.data,
      isMaster,
      updateImage,
      updateMaster,
      updatePane2,
    ],
  )

  const onImageLoadError = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper error type
    async (err: any) => {
      const errText =
        typeof err === 'string'
          ? err
          : err?.message != null
            ? String(err.message)
            : String(err ?? '')

      if (errText.includes('blob:')) {
        if (imageViewerStateDetail.data.processed_image_url) {
          try {
            const imageBlob = await fetchImage(
              imageViewerStateDetail.data.processed_image_url,
            )
            const url = createObjectURLSafe(imageBlob)
            if (url) {
              isMaster
                ? updateMaster({ image: url })
                : updatePane2({ image: url })
            } else {
              // eslint-disable-next-line no-console -- Keep visibility in console
              console.error('Could not load blob!')
            }
          } catch {
            // eslint-disable-next-line no-console -- Keep visibility in console
            console.error('Could not load blob!')
          }
        } else {
          // eslint-disable-next-line no-console -- Keep visibility in console
          console.error('Missing processed image URL!')
        }
        return
      }

      // Non-blob errors: log only — do not throw into ErrorBoundary (blank pane).
      // eslint-disable-next-line no-console -- Keep visibility in console
      console.error('Pannellum image load error:', errText || err)
    },
    [imageViewerStateDetail, isMaster, updateMaster, updatePane2],
  )

  const handleZoomChanged = useCallback(
    (level: number) => {
      onUpdate && onUpdate({ ...viewerPosition, hfov: level })
    },
    [onUpdate, viewerPosition],
  )

  const handleTakeScreenshot = useCallback(
    (callback: (blob: Blob) => void) => {
      takeScreenShot(mapPopupRef, viewer, callback)
    },
    [viewer],
  )

  // Register master pane's screenshot function so the subordinate pane
  // (ForgeViewer) can request a camera image for composite downloads
  useEffect(() => {
    if (isMaster && onRegisterScreenshot) {
      onRegisterScreenshot(handleTakeScreenshot)
    }
  }, [isMaster, onRegisterScreenshot, handleTakeScreenshot])

  useEffect(() => {
    if (!toggleActive) {
      setAdjustedOffset(undefined)
    }
  }, [toggleActive])

  useEffect(() => {
    if (!buildingState.imageData) {
      return
    }
    if (isMaster) {
      updateMaster({ ...buildingState.imageData })
    }
  }, [viewer, buildingState.imageData, isMaster, updateMaster])

  useEffect(() => {
    setLoading(false)
    if (!buildingState.imageData) {
      return
    }
    if (isMaster) {
      updateMaster({ ...buildingState.imageData })
    } else {
      updatePane2({ ...buildingState.imageData, angleDelta: 0 })
    }
  }, [buildingState.imageData, isMaster, updateMaster, updatePane2])

  const focusedClassName = useMemo(() => {
    return imageViewerState.focused === type && imageViewerState.splitScreen
      ? 'viewer-focused'
      : 'viewer'
  }, [imageViewerState.focused, imageViewerState.splitScreen, type])

  // Require this pane's image metadata before render. Split subordinate mounts
  // before updatePane2 fills `data`; ungated `.data.true_x` (minimap true-pose)
  // threw and took down the ErrorBoundary.
  const paneData = imageViewerState[type]?.data
  if (!paneData || (!isMaster && !viewerPosition)) return <></>

  if (loading)
    return (
      <div style={{ backgroundColor: '#f8f8f8', width: '100%' }}>
        <LoadingIndicator />
      </div>
    )

  return (
    <ErrorBoundary
      FallbackComponent={ComponentErrorFallback}
      onReset={() => {
        window.location.reload()
      }}
    >
      <div
        style={{ height: '100%', width: '100%' }}
        className={`${focusedClassName}`}
        onKeyDown={handleToggleKey}
        onClick={() => updateImageViewer({ focused: type })}
        id={'image-viewer-' + type}
        tabIndex={0}
      >
        {toggleActive && (
          <DebugPanel
            viewerPosition={viewerPosition}
            imageData={imageData}
            onOffsetChange={handleOffsetChange}
            onAngleSaved={handleAngleSavedToContext}
          />
        )}
        {isMaster && viewer && (
          <ProcoreAuthProvider>
            <ImageViewerContextMenu
              viewerId={viewerId}
              onTakeScreenshot={handleTakeScreenshot}
            />
            <TagManager
              viewerId={viewerId}
              onTakeScreenshot={handleTakeScreenshot}
              viewerPosition={viewerPosition}
            />
            <TourManager
              viewerId={viewerId}
              height={paneData.height}
              trueX={paneData.true_x}
              trueY={paneData.true_y}
              adjustedOffset={effectivePannellumAngleOffset}
            />
          </ProcoreAuthProvider>
        )}
        {!isMaster && imageViewerState.pane2Type === 'forge' ? (
          <XRayView
            viewerPosition={viewerPosition}
            viewerPositionDelta={viewerPositionDelta}
            followRealtime={followRealtime}
            onDragging={onDragging}
            onUpdate={onUpdate}
            onRequestCameraScreenshot={onRequestCameraScreenshot}
          />
        ) : (
          <Pannellum
            image={imageViewerState[type].image}
            viewerId={isMaster ? 'master' : 'subordinate'}
            isSolo={isSolo}
            viewerPosition={viewerPosition}
            viewerPositionDelta={viewerPositionDelta}
            followRealtime={followRealtime}
            onDragging={onDragging}
            onUpdate={onUpdate}
            angleOffset={effectivePannellumAngleOffset}
            onError={onImageLoadError}
            syncedTo={syncedTo}
          />
        )}
        <MinimapPopup
          ref={mapPopupRef}
          mapRef={mapRef}
          viewerPosition={viewerPosition}
          adjustedOffset={angleOffsetDelta}
          rightPosition={minimapRightPosition}
          type={type}
          minDate={minDate}
          maxDate={maxDate}
          trueX={paneData.true_x}
          trueY={paneData.true_y}
        />
        {isMaster && (
          <ImageViewerSettings
            viewerPosition={viewerPosition}
            zoomLevel={viewerPosition.hfov}
            zoomMin={50}
            zoomMax={120}
            zoomStep={10}
            onZoomChanged={handleZoomChanged}
            onTakeScreenshot={handleTakeScreenshot}
          />
        )}
      </div>
    </ErrorBoundary>
  )
}

function loadImage(image: string) {
  return new Promise<HTMLImageElement>(resolve => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.src = image
  })
}

export const takeScreenShot = async (
  mapPopupRef: React.RefObject<HTMLDivElement>,
  viewer: PannellumViewer | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper return type
  callback: (blob: Blob) => any,
) => {
  if (mapPopupRef.current && viewer) {
    const viewerImageData = viewer
      .getRenderer()
      .render(
        THREE.MathUtils.degToRad(viewer.getPitch()),
        THREE.MathUtils.degToRad(viewer.getYaw()),
        THREE.MathUtils.degToRad(viewer.getHfov()),
        { returnImage: true },
      )
    const canvas = document.createElement('canvas') as HTMLCanvasElement
    const context = canvas.getContext('2d')!
    const viewerImage = await loadImage(viewerImageData)
    canvas.width = viewerImage.width
    canvas.height = viewerImage.height
    context.drawImage(viewerImage, 0, 0, canvas.width, canvas.height)
    const canvasPopup = await html2canvas(mapPopupRef.current, {
      ignoreElements: el => el.classList.contains('ignore-html2canvas'),
      backgroundColor: FLOOR_PLAN_CHROME,
    })
    context.drawImage(canvasPopup, canvas.width - canvasPopup.width - 10, 10)
    canvas.toBlob(blob => {
      blob && callback(blob)
    })
    mixpanel.track('Download Screenshot')
  }
}
